import { create } from 'zustand';
import { getTutorialSteps, type PlayerRace, type UserProfile } from '@kaeliux/shared';
import { updateTutorialProgress } from '../services/profile.service';
import { HttpError } from '../services/httpError';
import { useAuthStore } from './authStore';
import { usePlanetStore } from './planetStore';

const ACTIVE_STORAGE_KEY = 'kaeliux-tutorial-active';

/** Highest valid step index for `race`'s path — steps and their count vary per race past the shared prefix. Defaults to the (always present) Humans path when `race` isn't known yet. */
function lastIndex(race: PlayerRace | undefined): number {
    return getTutorialSteps(race ?? 'humans').length - 1;
}

interface TutorialStoreState {
    /** Whether the popup is currently shown. Mirrored to localStorage (see below) so a reload while genuinely open doesn't silently close it. */
    isActive: boolean;
    stepIndex: number;
    disabled: boolean;
    /** Set once by `syncFromProfile` — determines which race's step path `stepIndex` indexes into. */
    race: PlayerRace | undefined;
    /**
     * Whether the *current* step's goal is satisfied right now — always true
     * for a manual step, otherwise `isTutorialStepComplete(...)` against live
     * game state. `TutorialOverlay` is the only thing that ever writes this
     * (it has the planet/science data needed to compute it, via a plain
     * `setState` call, same pattern `usePlanetStore`/`usePlayerStore` use
     * elsewhere); everything else — the Next button, the reward reveal, the
     * character icon's pulse — just reads it.
     */
    stepReady: boolean;
    /**
     * The step the server last refused to move past (a 4xx answer to the
     * progress write), or `null`. While it equals `stepIndex` the silent
     * background advance in `TutorialOverlay` stands down — otherwise a step
     * the client judges complete but the server doesn't would be re-sent in a
     * loop — and the open popup says why the move was refused. Cleared by
     * `next`, `back` and `open`, and whenever `stepIndex` changes.
     */
    refusedStepIndex: number | null;
    /** Seeds step/disabled state from the loaded profile — safe to call every render, only takes effect once per session. */
    syncFromProfile: (profile: UserProfile) => void;
    /** Opens the tutorial: resumes at the current step (re-enabling if it was disabled), or restarts from the top if it was already finished. */
    open: () => void;
    /** Hides the popup without touching progress or the enabled state — resumable later via `open`. */
    close: () => void;
    /** Hides the popup and marks the tutorial disabled — `open()` still resumes from the same step later, it doesn't restart. */
    disable: () => void;
    /** Moves to the next step. `planetId` is the planet the step was judged on; without it the planet on screen when called is sent. */
    next: (planetId?: string) => void;
    back: () => void;
}

let synced = false;

/**
 * `isActive` itself doesn't belong on the profile (it's not progress, just
 * "is the popup currently open") but it can't be pure in-memory state either
 * — a page reload re-mounts everything, and without this it would read back
 * as `false` even mid-tutorial, closing a popup the player never dismissed.
 * localStorage is enough: it only needs to survive this browser, not travel
 * across devices the way step/disabled do.
 */
function readStoredActive(): boolean {
    return localStorage.getItem(ACTIVE_STORAGE_KEY) === 'true';
}

function writeStoredActive(active: boolean): void {
    localStorage.setItem(ACTIVE_STORAGE_KEY, active ? 'true' : 'false');
}

/**
 * Writes step/disabled to the player's profile (mock or real, whichever is
 * aliased in), along with the planet on screen so the server can judge the
 * steps being passed. Fire-and-forget — the local state is the source of truth
 * for this session — except that a refusal (any 4xx: the step isn't complete on
 * the server, or the request couldn't be judged) puts `stepIndex` back to
 * `revertTo` and records it in `refusedStepIndex`, unless it has moved again
 * since. `planetId` defaults to the planet on screen when called.
 */
function persist(stepIndex: number, disabled: boolean, revertTo?: number, planetId?: string): void {
    const { user } = useAuthStore.getState();
    if (!user) return;
    const judgedOn = planetId ?? usePlanetStore.getState().currentPlanet?.id;
    updateTutorialProgress(user.uid, { stepIndex, disabled, ...(judgedOn ? { planetId: judgedOn } : {}) }).catch((err) => {
        const refused = err instanceof HttpError && err.status >= 400 && err.status < 500;
        if (refused && revertTo !== undefined && useTutorialStore.getState().stepIndex === stepIndex) {
            useTutorialStore.setState({ stepIndex: revertTo, refusedStepIndex: revertTo });
        }
        console.error('Failed to persist tutorial progress:', err);
    });
}

export const useTutorialStore = create<TutorialStoreState>((set, get) => ({
    isActive: readStoredActive(),
    stepIndex: 0,
    disabled: false,
    race: undefined,
    stepReady: false,
    refusedStepIndex: null,

    syncFromProfile: (profile) => {
        if (synced) return;
        synced = true;
        // A brand-new player (never interacted with the tutorial at all, but
        // already has a planet) gets it opened for them once; everyone else
        // only opens it by tapping the character icon — `isActive` above
        // already reflects whether it was left open across this reload, so
        // this only ever needs to turn it *on*, never off.
        const isBrandNew = profile.tutorialStepIndex === undefined && !!profile.mainPlanetId;
        const stepIndex = Math.min(profile.tutorialStepIndex ?? 0, lastIndex(profile.race));
        const disabled = profile.tutorialDisabled ?? false;
        set((state) => ({ stepIndex, disabled, race: profile.race, isActive: state.isActive || isBrandNew }));
        if (isBrandNew) writeStoredActive(true);
    },

    open: () => {
        const { stepIndex, disabled, race, stepReady } = get();
        // The last step is only finished once its goal is met; until then the
        // icon resumes there instead of starting the tutorial over.
        const restart = stepIndex >= lastIndex(race) && stepReady;
        const nextIndex = restart ? 0 : stepIndex;
        set({ isActive: true, disabled: false, stepIndex: nextIndex, refusedStepIndex: null });
        writeStoredActive(true);
        if (disabled || restart) persist(nextIndex, false);
    },

    close: () => {
        const { stepIndex, disabled } = get();
        set({ isActive: false });
        writeStoredActive(false);
        // Even a no-progress close (dismissed right at step 0) must persist
        // *something*, so a brand-new profile stops looking brand-new —
        // otherwise the next reload's auto-open would reopen a popup the
        // player just closed.
        persist(stepIndex, disabled);
    },

    disable: () => {
        set({ isActive: false, disabled: true });
        writeStoredActive(false);
        persist(get().stepIndex, true);
    },

    next: (planetId) => {
        const { stepIndex, disabled, race } = get();
        const nextIndex = Math.min(stepIndex + 1, lastIndex(race));
        if (nextIndex === stepIndex) return;
        set({ stepIndex: nextIndex, refusedStepIndex: null });
        persist(nextIndex, disabled, stepIndex, planetId);
    },

    back: () => {
        const { stepIndex, disabled } = get();
        const prevIndex = Math.max(stepIndex - 1, 0);
        if (prevIndex === stepIndex) return;
        set({ stepIndex: prevIndex, refusedStepIndex: null });
        persist(prevIndex, disabled);
    },
}));
